import { useEffect, useState } from 'react';
import api from '@/api';
import { Plus, Trash2, Users, ToggleLeft, ToggleRight, Edit2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const PlatformToggle = ({ label, color, checked, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <span className={`text-sm font-medium ${color}`}>{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', company_name: '', phone: '', country: '', platform_access: { demaxtore: true, commoditybid: true, freightiq: true } });

  const load = () => {
    api.get('/admin/customers').then(r => setCustomers(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/admin/customers', form);
      setCreateOpen(false);
      setForm({ name: '', email: '', password: '', company_name: '', phone: '', country: '', platform_access: { demaxtore: true, commoditybid: true, freightiq: true } });
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error creating customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer? This cannot be undone.')) return;
    await api.delete(`/admin/customers/${id}`);
    load();
  };

  const handleToggleStatus = async (id) => {
    await api.put(`/admin/customers/${id}/toggle-status`);
    load();
  };

  const openAccessDialog = (customer) => {
    setSelectedCustomer({ ...customer, platform_access: { ...customer.platform_access } });
    setAccessOpen(true);
  };

  const saveAccess = async () => {
    await api.put(`/admin/customers/${selectedCustomer._id}/platform-access`, selectedCustomer.platform_access);
    setAccessOpen(false);
    load();
  };

  const toggleAccess = (platform) => {
    setSelectedCustomer(prev => ({
      ...prev,
      platform_access: { ...prev.platform_access, [platform]: !prev.platform_access[platform] }
    }));
  };

  return (
    <div className="space-y-5" data-testid="admin-customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Customers</h1>
          <p className="text-slate-500 text-sm mt-0.5">{customers.length} registered customers</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-customer-button" className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4" /> Add Customer
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : customers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No customers yet</p>
          <Button onClick={() => setCreateOpen(true)} className="mt-4 bg-slate-900 hover:bg-slate-800" data-testid="create-first-customer-button">Add Customer</Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full" data-testid="customers-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Company</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platforms</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {customers.map(c => (
                <tr key={c._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.email}</p>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="text-sm text-slate-600">{c.company_name || '—'}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1 flex-wrap">
                      {c.platform_access?.demaxtore && <span className="text-xs font-semibold bg-sky-50 text-sky-700 px-2 py-0.5 rounded">DM</span>}
                      {c.platform_access?.commoditybid && <span className="text-xs font-semibold bg-orange-50 text-orange-700 px-2 py-0.5 rounded">CB</span>}
                      {c.platform_access?.freightiq && <span className="text-xs font-semibold bg-green-50 text-green-700 px-2 py-0.5 rounded">FQ</span>}
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${c.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openAccessDialog(c)}
                        data-testid={`manage-access-${c._id}`}
                        className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded transition-colors"
                        title="Manage Platform Access"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(c._id)}
                        data-testid={`toggle-status-${c._id}`}
                        className={`p-1.5 rounded transition-colors ${c.is_active ? 'text-slate-400 hover:text-amber-500 hover:bg-amber-50' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`}
                        title={c.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {c.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(c._id)}
                        data-testid={`delete-customer-${c._id}`}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Customer Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Name *</label>
                <Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" data-testid="customer-name-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Company</label>
                <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Company name" data-testid="customer-company-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Email *</label>
              <Input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="customer@company.com" data-testid="customer-email-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Password *</label>
              <Input required type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 6 characters" data-testid="customer-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Phone</label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 555-0000" data-testid="customer-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Country</label>
                <Input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder="United States" data-testid="customer-country-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Platform Access</label>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <PlatformToggle label="DeMaxtore" color="text-sky-600" checked={form.platform_access.demaxtore} onChange={v => setForm(f => ({ ...f, platform_access: { ...f.platform_access, demaxtore: v } }))} />
                <PlatformToggle label="CommodityBid" color="text-orange-600" checked={form.platform_access.commoditybid} onChange={v => setForm(f => ({ ...f, platform_access: { ...f.platform_access, commoditybid: v } }))} />
                <PlatformToggle label="FreightIQ" color="text-green-600" checked={form.platform_access.freightiq} onChange={v => setForm(f => ({ ...f, platform_access: { ...f.platform_access, freightiq: v } }))} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="customer-submit-button">
                {submitting ? 'Creating...' : 'Create Customer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Platform Access Dialog */}
      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        {selectedCustomer && (
          <DialogContent className="sm:max-w-sm" data-testid="platform-access-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Platform Access — {selectedCustomer.name}</DialogTitle>
            </DialogHeader>
            <div className="mt-2">
              <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                <PlatformToggle label="DeMaxtore" color="text-sky-600" checked={selectedCustomer.platform_access?.demaxtore ?? true} onChange={() => toggleAccess('demaxtore')} />
                <PlatformToggle label="CommodityBid" color="text-orange-600" checked={selectedCustomer.platform_access?.commoditybid ?? true} onChange={() => toggleAccess('commoditybid')} />
                <PlatformToggle label="FreightIQ" color="text-green-600" checked={selectedCustomer.platform_access?.freightiq ?? true} onChange={() => toggleAccess('freightiq')} />
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setAccessOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={saveAccess} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="save-platform-access-button">Save</Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
